function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import Log from '@deephaven/log';
import PanelEvent from "./PanelEvent.js";
import { listenForCycleToNextStack, listenForCycleToPreviousStack, listenForCycleToNextTab, listenForCycleToPreviousTab } from "./NavigationEvent.js";
import LayoutUtils, { isReactComponentConfig } from "./layout/LayoutUtils.js";
import { isWrappedComponent } from "./DashboardPlugin.js";
var log = Log.module('PanelManager');
var CycleDirection = /*#__PURE__*/function (CycleDirection) {
  CycleDirection[CycleDirection["Next"] = 0] = "Next";
  CycleDirection[CycleDirection["Previous"] = 1] = "Previous";
  return CycleDirection;
}(CycleDirection || {});
/**
 * Class to keep track of which panels are open, have been closed, and also events to close panels.
 */
class PanelManager {
  /**
   * @param layout The GoldenLayout object to attach to
   * @param hydrateComponent Function to hydrate a panel from a dehydrated state
   * @param dehydrateComponent Function to dehydrate a panel
   * @param openedMap Map of opened panels
   * @param closed Array of closed panels in a dehydrated state
   * @param onPanelsUpdated Triggered when the panels are updated
   */
  constructor(layout) {
    var hydrateComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : (name, props) => props;
    var dehydrateComponent = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : (name, config) => config;
    var openedMap = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : new Map();
    var closed = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : [];
    var onPanelsUpdated = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : () => undefined;
    _defineProperty(this, "layout", void 0);
    _defineProperty(this, "hydrateComponent", void 0);
    _defineProperty(this, "dehydrateComponent", void 0);
    _defineProperty(this, "onPanelsUpdated", void 0);
    _defineProperty(this, "closed", void 0);
    _defineProperty(this, "openedMap", void 0);
    _defineProperty(this, "navigationEventListenerRemovers", void 0);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleMount = this.handleMount.bind(this);
    // Panel can be unmounted on error but still keep the tab until it's closed
    // Use PanelEvent.MOUNT/UNMOUNT to track open (active) panels
    // and PanelEvent.CLOSED for cleanup (delete links, add panel to closed panels list, etc)
    this.handleUnmount = this.handleUnmount.bind(this);
    this.handleReopen = this.handleReopen.bind(this);
    this.handleReopenLast = this.handleReopenLast.bind(this);
    this.handleCycleToNextStack = this.handleCycleToNextStack.bind(this);
    this.handleCycleToPreviousStack = this.handleCycleToPreviousStack.bind(this);
    this.handleCycleToNextTab = this.handleCycleToNextTab.bind(this);
    this.handleCycleToPreviousTab = this.handleCycleToPreviousTab.bind(this);
    this.handleDeleted = this.handleDeleted.bind(this);
    this.handleClosed = this.handleClosed.bind(this);
    this.handleControlClose = this.handleControlClose.bind(this);
    this.layout = layout;
    this.hydrateComponent = hydrateComponent;
    this.dehydrateComponent = dehydrateComponent;
    this.onPanelsUpdated = onPanelsUpdated;

    // Store the opened and closed panels
    this.openedMap = new Map(openedMap);

    // Closed panels are stored in their dehydrated state
    this.closed = [...closed];

    // Store the navigation event listener removers
    this.navigationEventListenerRemovers = [];
    this.startListening();
  }
  startListening() {
    var eventHub = this.layout.eventHub;
    eventHub.on(PanelEvent.FOCUS, this.handleFocus);
    eventHub.on(PanelEvent.MOUNT, this.handleMount);
    eventHub.on(PanelEvent.UNMOUNT, this.handleUnmount);
    eventHub.on(PanelEvent.REOPEN, this.handleReopen);
    eventHub.on(PanelEvent.REOPEN_LAST, this.handleReopenLast);
    eventHub.on(PanelEvent.DELETE, this.handleDeleted);
    eventHub.on(PanelEvent.CLOSED, this.handleClosed);
    eventHub.on(PanelEvent.CLOSE, this.handleControlClose);
    // PanelEvent.OPEN should be listened to by plugins to open a panel

    this.navigationEventListenerRemovers.push(listenForCycleToNextStack(eventHub, this.handleCycleToNextStack));
    this.navigationEventListenerRemovers.push(listenForCycleToPreviousStack(eventHub, this.handleCycleToPreviousStack));
    this.navigationEventListenerRemovers.push(listenForCycleToNextTab(eventHub, this.handleCycleToNextTab));
    this.navigationEventListenerRemovers.push(listenForCycleToPreviousTab(eventHub, this.handleCycleToPreviousTab));
  }
  stopListening() {
    var eventHub = this.layout.eventHub;
    eventHub.off(PanelEvent.FOCUS, this.handleFocus);
    eventHub.off(PanelEvent.MOUNT, this.handleMount);
    eventHub.off(PanelEvent.UNMOUNT, this.handleUnmount);
    eventHub.off(PanelEvent.REOPEN, this.handleReopen);
    eventHub.off(PanelEvent.REOPEN_LAST, this.handleReopenLast);
    eventHub.off(PanelEvent.DELETE, this.handleDeleted);
    eventHub.off(PanelEvent.CLOSED, this.handleClosed);
    eventHub.off(PanelEvent.CLOSE, this.handleControlClose);
    this.navigationEventListenerRemovers.forEach(remover => remover());
    this.navigationEventListenerRemovers = [];
  }
  getClosedPanelConfigsOfType(typeString) {
    return this.closed.filter(panel => panel.component === typeString);
  }
  getOpenedPanels() {
    return Array.from(this.openedMap.values());
  }
  getOpenedPanelConfigs() {
    return this.getOpenedPanels().map(panel => {
      var glContainer = panel.props.glContainer;
      return LayoutUtils.getComponentConfigFromContainer(glContainer);
    });
  }
  getOpenedPanelConfigsOfType(typeString) {
    return this.getOpenedPanelConfigs().filter(config => config != null && isReactComponentConfig(config) && config.component === typeString);
  }
  getOpenedPanelById(panelId) {
    return this.openedMap.get(panelId);
  }
  getContainerByPanelId(panelId) {
    var _ref;
    var stack = LayoutUtils.getStackForConfig(this.layout.root, {
      id: panelId
    });
    return (_ref = stack && LayoutUtils.getContentItemInStack(stack, {
      id: panelId
    })) !== null && _ref !== void 0 ? _ref : null;
  }
  getLastUsedPanel(matcher) {
    var opened = this.getOpenedPanels();
    for (var i = opened.length - 1; i >= 0; i -= 1) {
      var panel = opened[i];
      if (matcher == null || matcher(panel)) {
        return panel;
      }
    }
    return undefined;
  }
  getLastUsedPanelOfType(type) {
    return this.getLastUsedPanelOfTypes([type]);
  }
  getLastUsedPanelOfTypes(types) {
    return this.getLastUsedPanel(panel => types.some(type => isWrappedComponent(type) && panel instanceof type.WrappedComponent || !isWrappedComponent(type) && panel instanceof type));
  }
  updatePanel(panel) {
    var panelId = LayoutUtils.getIdFromPanel(panel);
    if (panelId == null) {
      log.error('updatePanel Panel did not have an ID', panel);
      return;
    }
    log.debug2("Updating panel ID ".concat(panelId, " in open panels map"));
    // Delete the entry before it's set to maintain correct ordering in the open panels map.
    // The last updated (focused) panel should be the last inserted.
    // Deleting the entry from the map directly instead of calling this.removePanel to skip the checks.
    this.openedMap.delete(panelId);
    this.openedMap.set(panelId, panel);
  }
  removePanel(panel) {
    var panelId = LayoutUtils.getIdFromPanel(panel);
    if (panelId == null) {
      log.error('removePanel Panel did not have an ID', panel);
      return;
    }
    if (!this.openedMap.has(panelId)) {
      log.error("Missing panel ID ".concat(panelId, " in open panels map"));
      return;
    }
    if (this.openedMap.get(panelId) !== panel) {
      // We mount a new panel before un-mounting the existing one
      // when replacing existing panels in openComponent/openComponentInStack.
      // Skip map delete if the panelId entry already refers to the new panel.
      log.debug("Panel argument doesn't match the open panels map entry for ".concat(panelId, ", removePanel ignored"));
      return;
    }
    log.debug2("Removing panel ID ".concat(panelId, " from open panels map"));
    this.openedMap.delete(panelId);
  }
  removeClosedPanelConfig(panelConfig) {
    var index = this.closed.findIndex(closedConfig => closedConfig === panelConfig || closedConfig.id != null && panelConfig.id != null && closedConfig.id === panelConfig.id);
    if (index >= 0) {
      this.closed.splice(index, 1);
    }
  }
  handleFocus(panel) {
    log.debug2('Focus: ', panel);
    this.updatePanel(panel);
  }
  handleMount(panel) {
    log.debug2('Mount: ', panel);
    this.updatePanel(panel);
    this.sendUpdate();
  }
  handleUnmount(panel) {
    log.debug2('Unmount: ', panel);
    this.removePanel(panel);
    this.sendUpdate();
  }
  cycleStack(direction) {
    var allStacks = LayoutUtils.getAllStackContainers(this.layout);
    if (allStacks.length <= 1) {
      return;
    }
    var focusedIndex = LayoutUtils.getFocusedStackIndex(allStacks);

    // If no stack is focused, activate the first stack's content item
    if (focusedIndex === -1) {
      var _targetStack = allStacks[0];
      var _activeContentIndex = _targetStack.config.activeItemIndex;
      var _activeContentItem = _activeContentIndex != null ? _targetStack.contentItems[_activeContentIndex] : _targetStack.contentItems[0];
      _targetStack.setActiveContentItem(_activeContentItem, true);
      return;
    }
    var targetIndex = direction === CycleDirection.Next ? (focusedIndex + 1) % allStacks.length : (focusedIndex - 1 + allStacks.length) % allStacks.length;
    var targetStack = allStacks[targetIndex];
    var activeContentIndex = targetStack.config.activeItemIndex;
    var activeContentItem = activeContentIndex != null ? targetStack.contentItems[activeContentIndex] : targetStack.contentItems[0];
    targetStack.setActiveContentItem(activeContentItem, true);
  }
  handleCycleToNextStack() {
    this.cycleStack(CycleDirection.Next);
  }
  handleCycleToPreviousStack() {
    this.cycleStack(CycleDirection.Previous);
  }
  cycleTab(direction) {
    var _focusedStack$config$;
    var focusedStack = LayoutUtils.getFocusedStack(this.layout);
    if (focusedStack === undefined) {
      return;
    }
    var contentItems = focusedStack.contentItems;
    if (contentItems.length <= 1) {
      return;
    }
    var activeItemIndex = (_focusedStack$config$ = focusedStack.config.activeItemIndex) !== null && _focusedStack$config$ !== void 0 ? _focusedStack$config$ : 0;
    var targetIndex = direction === CycleDirection.Next ? (activeItemIndex + 1) % contentItems.length : (activeItemIndex - 1 + contentItems.length) % contentItems.length;
    var targetContentItem = contentItems[targetIndex];
    focusedStack.setActiveContentItem(targetContentItem, true);
  }
  handleCycleToNextTab() {
    this.cycleTab(CycleDirection.Next);
  }
  handleCycleToPreviousTab() {
    this.cycleTab(CycleDirection.Previous);
  }

  /**
   *
   * @param panelConfig The config to hydrate and load
   * @param replaceConfig The config to place
   */
  handleReopen(panelConfig, replaceConfig) {
    log.debug2('Reopen:', panelConfig, replaceConfig);
    this.removeClosedPanelConfig(panelConfig);
    // Don't need to send an update yet, it will get sent when component is mounted

    // Rehydrate the panel before adding it back
    var component = panelConfig.component;
    var props = panelConfig.props;
    props = this.hydrateComponent(component, props);
    var config = _objectSpread(_objectSpread({}, panelConfig), {}, {
      props
    });
    var root = this.layout.root;
    var stack = panelConfig.parentStackId === undefined ? undefined : LayoutUtils.getStackById(root, panelConfig.parentStackId);
    LayoutUtils.openComponent({
      root,
      config,
      replaceConfig,
      stack: stack !== null && stack !== void 0 ? stack : undefined
    });
  }

  /**
   *
   * @param glContainer Only reopen panels that were closed from the stack of this container, if defined
   */
  handleReopenLast(glContainer) {
    var _LayoutUtils$getStack;
    if (this.closed.length === 0) return;
    if (glContainer === undefined) {
      this.handleReopen(this.closed[this.closed.length - 1]);
      return;
    }
    var stackId = (_LayoutUtils$getStack = LayoutUtils.getStackForConfig(this.layout.root, glContainer.getConfig())) === null || _LayoutUtils$getStack === void 0 ? void 0 : _LayoutUtils$getStack.config.id;
    for (var i = this.closed.length - 1; i >= 0; i -= 1) {
      var panelConfig = this.closed[i];
      if (panelConfig.parentStackId === stackId) {
        this.handleReopen(panelConfig);
        return;
      }
    }
  }
  handleDeleted(panelConfig) {
    log.debug2('Deleted:', panelConfig);
    this.removeClosedPanelConfig(panelConfig);
    this.sendUpdate();
  }
  handleClosed(panelId, glContainer) {
    // Panel component should be already unmounted at this point
    // so the emitted event sends the container object instead of the panel.
    log.debug2('Closed: ', panelId);
    this.addClosedPanel(glContainer);
    this.sendUpdate();
  }
  handleControlClose(id) {
    var config = {
      id
    };
    var root = this.layout.root;
    LayoutUtils.closeComponent(root, config);
  }
  addClosedPanel(glContainer) {
    var config = LayoutUtils.getComponentConfigFromContainer(glContainer);
    if (config && isReactComponentConfig(config)) {
      var dehydratedConfig = this.dehydrateComponent(config.component, config);
      if (dehydratedConfig != null) {
        var _glContainer$tab;
        // Not using LayoutUtils.getStackForConfig here because the panel may be removed from the root config already.
        // The container config should be the same as it was when it was unmounted, so this avoids the issue of the config not existing on root.
        dehydratedConfig.parentStackId = (_glContainer$tab = glContainer.tab) === null || _glContainer$tab === void 0 ? void 0 : _glContainer$tab.header.parent.config.id;
        this.closed.push(dehydratedConfig);
      }
    }
  }
  sendUpdate() {
    var closed = this.closed,
      openedMap = this.openedMap;
    this.onPanelsUpdated({
      closed: [...closed],
      openedMap: new Map(openedMap)
    });
  }
}
_defineProperty(PanelManager, "MAX_CLOSED_PANEL_COUNT", 100);
export default PanelManager;
//# sourceMappingURL=PanelManager.js.map